import { z } from 'zod';

const oobInteractionSchema = z.object({
  id: z.string(),
  protocol: z.enum(['http', 'dns', 'smtp', 'ftp']),
  uniqueId: z.string(),
  fullId: z.string(),
  timestamp: z.string(),
  remoteAddress: z.string(),
  rawRequest: z.string().optional(),
  rawResponse: z.string().optional(),
  qType: z.string().optional(),
  httpMethod: z.string().optional(),
  httpPath: z.string().optional(),
  httpHeaders: z.record(z.string(), z.string()).optional(),
  httpBody: z.string().optional(),
});

export const oobEvidencePayloadSchema = z.discriminatedUnion('mode', [
  z.object({
    schemaVersion: z.literal(1),
    stepId: z.string(),
    mode: z.literal('1'),
    domain: z.string(),
    message: z.string(),
  }),
  z.object({
    schemaVersion: z.literal(1),
    stepId: z.string(),
    mode: z.literal('2'),
    sessionId: z.string(),
    count: z.number(),
    interactions: z.array(oobInteractionSchema),
    message: z.string().optional(),
  }),
  z.object({
    schemaVersion: z.literal(1),
    stepId: z.string(),
    mode: z.literal('3'),
    sessionId: z.string(),
    dynamicUrl: z.string(),
  }),
]);

export type OobEvidencePayload = z.infer<typeof oobEvidencePayloadSchema>;
export type OobInteraction = z.infer<typeof oobInteractionSchema>;

export function parseOobEvidencePayload(
  output: unknown,
): OobEvidencePayload | undefined {
  const candidate = normalizeOutput(output);

  if (candidate === undefined) {
    return undefined;
  }

  const parsed = oobEvidencePayloadSchema.safeParse(candidate);

  return parsed.success ? parsed.data : undefined;
}

function normalizeOutput(output: unknown): unknown {
  if (typeof output !== 'string') {
    return output;
  }

  const trimmed = output.trim();

  if (!trimmed) {
    return undefined;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}
